import { useState, useEffect } from 'react';
import { Plus, X } from 'lucide-react';
import { FamilyMember } from '@/types';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';

interface TreeNodeDialogProps {
  mode: 'edit' | 'add' | 'delete' | null;
  member: FamilyMember;
  open: boolean;
  onClose: () => void;
  onConfirm: (names?: string[]) => void;
}

export function TreeNodeDialog({ mode, member, open, onClose, onConfirm }: TreeNodeDialogProps) {
  const [names, setNames] = useState<string[]>(['']);

  // Initialize names based on mode
  useEffect(() => {
    if (mode === 'edit') {
      setNames([member.name]);
    } else if (mode === 'add') {
      setNames(['']);
    }
  }, [mode, member.name]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (mode === 'delete') {
      onConfirm([member.name]); // Pass the name for delete mode
    } else {
      const validNames = names.filter(name => name.trim());
      if (validNames.length > 0) {
        onConfirm(validNames);
      }
    }
  };

  const addNameField = () => {
    setNames([...names, '']);
  };

  const removeName = (index: number) => {
    setNames(names.filter((_, i) => i !== index));
  };

  const updateName = (index: number, value: string) => {
    const newNames = [...names];
    newNames[index] = value;
    setNames(newNames);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {mode === 'edit' ? 'تعديل الاسم' :
             mode === 'add' ? 'إضافة مولود جديد' :
             'تأكيد الحذف'}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit}>
          {mode === 'delete' ? (
            <div className="py-4">
              <p className="text-sm text-red-500 font-medium">
                تحذير: سيتم حذف {member.name} وجميع الأفراد المرتبطين به.
              </p>
              <p className="text-sm text-gray-500 mt-2">
                هذا الإجراء لا يمكن التراجع عنه.
              </p>
            </div>
          ) : mode === 'edit' ? (
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="name">الاسم</Label>
                <Input
                  id="name"
                  value={names[0]}
                  onChange={(e) => updateName(0, e.target.value)}
                  placeholder="أدخل الاسم"
                  autoFocus
                />
              </div>
            </div>
          ) : (
            <div className="space-y-4 py-4">
              {names.map((name, index) => (
                <div key={index} className="flex gap-2">
                  <Input
                    value={name}
                    onChange={(e) => updateName(index, e.target.value)}
                    placeholder={`اسم المولود ${index + 1}`}
                    autoFocus={index === 0}
                  />
                  {names.length > 1 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => removeName(index)}
                      className="flex-shrink-0"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              ))}

              <Button
                type="button"
                variant="outline"
                onClick={addNameField}
                className="w-full"
              >
                <Plus className="h-4 w-4 ml-2" />
                إضافة مولود آخر
              </Button>
            </div>
          )}

          <DialogFooter className="gap-2">
            {mode === 'delete' ? (
              <>
                <Button 
                  type="submit"
                  variant="destructive"
                >
                  تأكيد الحذف
                </Button>
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={onClose}
                >
                  إلغاء
                </Button>
              </>
            ) : (
              <>
                <Button 
                  type="submit"
                  disabled={!names.some(name => name.trim())}
                >
                  {mode === 'edit' ? 'حفظ' : 'إضافة'}
                </Button>
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={onClose}
                >
                  إلغاء
                </Button>
              </>
            )}
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}